import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const BASE = process.env.REACT_APP_API_URL || "";

const get = async (path) => {
  const res = await fetch(`${BASE}/api/portal${path}`);
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const Badge = ({ children, colour }) => (
  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] ${colour}`}>{children}</span>
);

const statusColour = (s) => {
  const m = { active: "bg-[#00F5D4]/10 text-[#00F5D4]", completed: "bg-[#4B5563] text-[#9CA3AF]", on_hold: "bg-[#F59E0B]/10 text-[#F59E0B]", cancelled: "bg-[#FF007A]/10 text-[#FF007A]" };
  return m[s] || "bg-[#1F2937] text-[#6B7280]";
};

const deliveryRiskMeta = (risk) => {
  const m = {
    low: { label: "On track", colour: "bg-[#00F5D4]/10 text-[#00F5D4]" },
    medium: { label: "Watch", colour: "bg-[#F59E0B]/10 text-[#F59E0B]" },
    high: { label: "At risk", colour: "bg-[#FF007A]/10 text-[#FF007A]" },
    critical: { label: "Critical", colour: "bg-[#FF007A]/20 text-[#FF007A]" },
  };
  return m[risk] || { label: "Unknown", colour: "bg-[#1F2937] text-[#6B7280]" };
};

const invoiceColour = (s) => {
  const m = { draft: "text-[#6B7280]", sent: "text-[#F59E0B]", paid: "text-[#00F5D4]", overdue: "text-[#FF007A]", void: "text-[#374151]" };
  return m[s] || "text-[#6B7280]";
};

const fmtMoney = (n, currency = "GBP") =>
  n == null ? "—" : new Intl.NumberFormat(undefined, { style: "currency", currency }).format(n);

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—";

const ProgressBar = ({ pct }) => (
  <div className="w-full h-1.5 bg-[#1F2937] rounded-full overflow-hidden">
    <div style={{ width: `${Math.min(pct, 100)}%` }} className="h-full bg-[#00F5D4] rounded-full transition-all" />
  </div>
);

const ClientPortal = () => {
  const { token } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [raidItems, setRaidItems] = useState([]);
  const [delivery, setDelivery] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    const load = async () => {
      try {
        const [p, t, m, inv, raid] = await Promise.all([
          get(`/view/${token}`),
          get(`/view/${token}/tasks`),
          get(`/view/${token}/milestones`),
          get(`/view/${token}/invoices`),
          get(`/view/${token}/raid`),
        ]);
        setProject(p);
        setTasks(t);
        setMilestones(m);
        setInvoices(inv);
        setRaidItems(raid);
      } catch {
        setError("This link is invalid or has expired. Please contact your project manager.");
        return;
      }
      // Delivery forecast is a nice-to-have — don't block the portal on it.
      get(`/view/${token}/delivery-forecast`).then(setDelivery).catch(() => {});
    };
    load();
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-center space-y-3">
          <div className="text-[#FF007A] text-sm">{error}</div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="text-xs uppercase tracking-[0.25em] text-[#6B7280]">Loading…</div>
      </div>
    );
  }

  const TABS = [
    { k: "overview", l: "Overview" },
    { k: "tasks", l: `Tasks (${tasks.length})` },
    { k: "milestones", l: `Milestones (${milestones.length})` },
    { k: "invoices", l: `Invoices (${invoices.length})` },
    ...(raidItems.length > 0 ? [{ k: "risks", l: `Risks & Issues (${raidItems.length})` }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#030712] text-white">
      {/* Header */}
      <div className="border-b border-[#1F2937] bg-[#0D1117]">
        <div className="max-w-5xl mx-auto px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#6B7280] mb-1">Client Portal</div>
              <h1 className="text-xl font-semibold tracking-tight">{project.name}</h1>
              {project.description && (
                <p className="text-sm text-[#6B7280] mt-1 max-w-xl">{project.description}</p>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge colour={statusColour(project.status)}>{project.status || "active"}</Badge>
              {project.health && (
                <Badge colour={project.health === "green" ? "bg-[#00F5D4]/10 text-[#00F5D4]" : project.health === "amber" ? "bg-[#F59E0B]/10 text-[#F59E0B]" : "bg-[#FF007A]/10 text-[#FF007A]"}>
                  {project.health}
                </Badge>
              )}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Start</div>
              <div className="text-sm mt-0.5">{fmtDate(project.start_date)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">End</div>
              <div className="text-sm mt-0.5">{fmtDate(project.end_date)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Contract value</div>
              <div className="text-sm mt-0.5 font-mono">{fmtMoney(project.contract_value, project.currency)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Progress</div>
              <ProgressBar pct={project.percent_complete || 0} />
              <div className="text-xs text-[#9CA3AF] mt-1">{project.percent_complete || 0}% complete</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-5xl mx-auto px-6 flex gap-0">
          {TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-4 py-3 text-xs uppercase tracking-[0.15em] border-b-2 transition-colors ${
                tab === t.k ? "border-[#00F5D4] text-[#00F5D4]" : "border-transparent text-[#6B7280] hover:text-white"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        {tab === "overview" && (
          <div className="space-y-4">
            <div className="text-sm text-[#6B7280]">
              Welcome to your project portal. Use the tabs above to view tasks, milestones, and invoices.
            </div>
            {delivery && (
              <div className="border border-[#1F2937] bg-[#0D1117] p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280]">Delivery outlook</div>
                  <Badge colour={deliveryRiskMeta(delivery.delivery.risk).colour}>
                    {deliveryRiskMeta(delivery.delivery.risk).label}
                  </Badge>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">On-time likelihood</div>
                    <div className="text-lg mt-0.5 font-mono">{delivery.delivery.on_time_probability}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Work complete</div>
                    <div className="text-lg mt-0.5 font-mono">{delivery.complete_pct}%</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280]">Overdue tasks</div>
                    <div className={`text-lg mt-0.5 font-mono ${delivery.delivery.overdue_tasks > 0 ? "text-[#FF007A]" : ""}`}>{delivery.delivery.overdue_tasks}</div>
                  </div>
                </div>
              </div>
            )}
            {milestones.length > 0 && (
              <div className="border border-[#1F2937] bg-[#0D1117] p-5">
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-3">Upcoming milestones</div>
                <div className="space-y-2">
                  {milestones.slice(0, 5).map((m) => (
                    <div key={m.milestone_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${m.completed ? "bg-[#00F5D4]" : "bg-[#374151]"}`} />
                        <span className={`text-sm ${m.completed ? "text-[#6B7280] line-through" : "text-white"}`}>{m.title}</span>
                      </div>
                      <span className="text-xs text-[#6B7280] font-mono">{fmtDate(m.due_date)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "tasks" && (
          <div className="border border-[#1F2937]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1F2937]">
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Task</th>
                  <th className="text-center px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Status</th>
                  <th className="text-center px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Priority</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Due</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.task_id} className="border-b border-[#1F2937] hover:bg-[#0D1117]">
                    <td className="px-4 py-2.5 text-white">{t.title}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="text-[10px] capitalize text-[#9CA3AF]">{t.status}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`text-[10px] capitalize ${t.priority === "high" || t.priority === "critical" ? "text-[#FF007A]" : t.priority === "medium" ? "text-[#F59E0B]" : "text-[#6B7280]"}`}>
                        {t.priority || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-[#6B7280] font-mono">{fmtDate(t.due_date)}</td>
                  </tr>
                ))}
                {tasks.length === 0 && (
                  <tr><td colSpan={4} className="py-12 text-center text-sm text-[#6B7280]">No tasks yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "milestones" && (
          <div className="border border-[#1F2937]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1F2937]">
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Milestone</th>
                  <th className="text-center px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Status</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Due date</th>
                </tr>
              </thead>
              <tbody>
                {milestones.map((m) => (
                  <tr key={m.milestone_id} className="border-b border-[#1F2937] hover:bg-[#0D1117]">
                    <td className="px-4 py-2.5 text-white">{m.title}</td>
                    <td className="px-4 py-2.5 text-center">
                      <Badge colour={m.completed ? "text-[#00F5D4]" : "text-[#6B7280]"}>
                        {m.completed ? "Complete" : "Pending"}
                      </Badge>
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-[#6B7280] font-mono">{fmtDate(m.due_date)}</td>
                  </tr>
                ))}
                {milestones.length === 0 && (
                  <tr><td colSpan={3} className="py-12 text-center text-sm text-[#6B7280]">No milestones yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "invoices" && (
          <div className="border border-[#1F2937]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1F2937]">
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Invoice #</th>
                  <th className="text-center px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Status</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Amount</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Due</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_id} className="border-b border-[#1F2937] hover:bg-[#0D1117]">
                    <td className="px-4 py-2.5 text-white font-mono text-xs">{inv.invoice_number}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`text-[10px] uppercase capitalize ${invoiceColour(inv.lifecycle_status)}`}>
                        {inv.lifecycle_status || "draft"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-sm">{fmtMoney(inv.total, inv.currency)}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-[#6B7280] font-mono">{fmtDate(inv.due_date)}</td>
                  </tr>
                ))}
                {invoices.length === 0 && (
                  <tr><td colSpan={4} className="py-12 text-center text-sm text-[#6B7280]">No invoices yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === "risks" && (
          <div className="border border-[#1F2937]">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#1F2937]">
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Type</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Title</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Status</th>
                  <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Owner</th>
                  <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-[#6B7280] bg-[#0D1117]">Due</th>
                </tr>
              </thead>
              <tbody>
                {raidItems.map((item) => (
                  <tr key={item.raid_id} className="border-b border-[#1F2937] hover:bg-[#0D1117]">
                    <td className="px-4 py-2.5">
                      <span className={`text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 ${
                        item.type === "risk" ? "bg-[#FF007A]/10 text-[#FF007A]" :
                        item.type === "issue" ? "bg-[#F59E0B]/10 text-[#F59E0B]" :
                        item.type === "action" ? "bg-[#002FA7]/20 text-[#60A5FA]" :
                        "bg-[#00F5D4]/10 text-[#00F5D4]"
                      }`}>{item.type || "item"}</span>
                    </td>
                    <td className="px-4 py-2.5 text-white">
                      <div className="font-medium">{item.title}</div>
                      {item.description && <div className="text-xs text-[#6B7280] mt-0.5 line-clamp-1">{item.description}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-[10px] capitalize text-[#9CA3AF]">{item.status || "open"}</span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[#9CA3AF]">{item.owner || "—"}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-[#6B7280] font-mono">{fmtDate(item.due_date)}</td>
                  </tr>
                ))}
                {raidItems.length === 0 && (
                  <tr><td colSpan={5} className="py-12 text-center text-sm text-[#6B7280]">No open risks or issues.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-8 text-center">
        <span className="text-[10px] text-[#374151]">Powered by CombinedWork · Read-only client view</span>
      </div>
    </div>
  );
};

export default ClientPortal;
